import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

type Axis = 'down' | 'right';

interface SwipeSheetProps {
  axis: Axis;
  onClose: () => void;
  /** Fires when dismiss animation starts (before onClose) — sync live band expand. */
  onExitStart?: () => void;
  children: ReactNode;
  className?: string;
  dimClassName?: string;
  threshold?: number;
}

const SwipeCloseContext = createContext<(() => void) | null>(null);

export function useSwipeClose(fallback?: () => void) {
  const ctx = useContext(SwipeCloseContext);
  return ctx || fallback || (() => undefined);
}

/**
 * Animated overlay sheet with swipe-to-dismiss.
 * - axis=down: product / bag — only the expanded grab handle starts dismiss (no scroll fight)
 * - axis=right: cart — swipe right to close
 */
export function SwipeSheet({
  axis,
  onClose,
  onExitStart,
  children,
  className = '',
  dimClassName = 'il-overlay-dim',
  threshold = 80,
}: SwipeSheetProps) {
  const start = useRef<{ x: number; y: number; fromGrab: boolean } | null>(null);
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [phase, setPhase] = useState<'enter' | 'open' | 'exit'>('enter');
  const closedRef = useRef(false);
  const offsetRef = useRef(0);
  const onExitStartRef = useRef(onExitStart);
  onExitStartRef.current = onExitStart;

  useEffect(() => {
    const id = requestAnimationFrame(() => setPhase('open'));
    return () => cancelAnimationFrame(id);
  }, []);

  const finishClose = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    setPhase('exit');
    onExitStartRef.current?.();
    const vv = window.visualViewport;
    const distance =
      axis === 'down'
        ? vv?.height || window.innerHeight
        : window.innerWidth;
    offsetRef.current = distance;
    setOffset(distance);
    window.setTimeout(() => onClose(), 320);
  }, [axis, onClose]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (phase === 'exit') return;
    const target = e.target as HTMLElement;
    if (
      target.closest(
        'input, textarea, button, a, select, .il-option-pills, .il-pdp-thumbs, .il-voucher-rail, .il-cart-mount, .il-cart-modal-body, .il-combo-carousel, .il-combo-steps'
      )
    ) {
      return;
    }

    const fromGrab = Boolean(target.closest('.il-sheet-grab, .il-sheet-handle'));

    if (axis === 'down') {
      // Product / bag: dismiss only via expanded grab zone — never fight sheet scroll
      if (!fromGrab) return;
      e.preventDefault();
    }

    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    start.current = { x: e.clientX, y: e.clientY, fromGrab };
    setDragging(true);
    offsetRef.current = 0;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!start.current || phase === 'exit') return;
    const dx = e.clientX - start.current.x;
    const dy = e.clientY - start.current.y;

    if (axis === 'down') {
      if (!start.current.fromGrab) return;
      e.preventDefault();
      const next = Math.max(0, dy);
      offsetRef.current = next;
      setOffset(next);
    } else {
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 14) return;
      const next = Math.max(0, dx);
      offsetRef.current = next;
      setOffset(next);
    }
  };

  const onPointerUp = () => {
    if (!start.current) return;
    const fromGrab = start.current.fromGrab;
    start.current = null;
    setDragging(false);
    const current = offsetRef.current;
    if (current > threshold || (fromGrab && axis === 'down' && current > 40)) {
      finishClose();
    } else {
      offsetRef.current = 0;
      setOffset(0);
    }
  };

  const transform =
    axis === 'down' ? `translate3d(0, ${offset}px, 0)` : `translate3d(${offset}px, 0, 0)`;

  const useInlineTransform = dragging || offset > 0 || phase === 'exit';

  const panelClass = [
    'il-swipe-panel',
    axis === 'down' ? 'il-swipe-panel-down' : 'il-swipe-panel-right',
    phase === 'open' && !dragging && offset === 0 ? 'is-in' : '',
    phase === 'exit' ? 'is-out' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <SwipeCloseContext.Provider value={finishClose}>
      <div
        className={`${dimClassName}${phase !== 'enter' ? ' is-visible' : ''}${
          phase === 'exit' ? ' is-hiding' : ''
        }`}
        onClick={(e) => {
          if (e.target === e.currentTarget) finishClose();
        }}
      >
        <div
          className={panelClass}
          style={{
            transform: useInlineTransform ? transform : undefined,
            transition: dragging
              ? 'none'
              : 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </SwipeCloseContext.Provider>
  );
}
